/*
 * Filter persistence for the Sales Task List page.
 *
 * The filters the page restores across reloads — status, task type, scope, L3
 * owner and (conditionally) the due-date range — live in localStorage under a
 * versioned key, following the reminders page's convention
 * ('reminders_filters_v1' → 'sales_task_list_filters_v1'). The level is never
 * persisted.
 *
 * Stored shape:
 *   {
 *     v: 4,
 *     status_filter: 'Pending',
 *     type_filter: ['FRESH_LEAD', 'NO_BOOK'],
 *     scope_filter: 'all' | 'unclaimed' | 'mine' | 'my_students',
 *     owner_filter: ['__unassigned__', '<cms uuid>'],
 *     call_filter: '' | 'eligible' | 'not_eligible',
 *     date_range: null | { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
 *   }
 *
 * v1 stored type_filter as a single string; v2 made it an array ([] = all
 * types). v3 drops agent_filter (the "Assigned to" filter replaced by scope and
 * owner). v4 adds call_filter ('' = any). Older blobs are upgraded step by step rather than discarded.
 *
 * date_range stays null while the user has not touched the date filter, so the
 * seeded yesterday→tomorrow window remains ROLLING. Once they pick a range — or
 * clear it, stored as { from: '', to: '' } — that explicit choice is persisted
 * verbatim.
 *
 * Every read is defensive: absent/corrupt JSON, a different schema version, or a
 * value that is no longer valid yields a fallback rather than throwing. An empty
 * selection is NOT treated as missing — clearing a filter is a deliberate choice
 * and must survive a reload.
 */

// The localStorage key is deliberately NOT versioned alongside the schema — the
// `v` field inside the blob is the version, which is what lets a v1 blob be read
// and upgraded in place instead of being orphaned under an old key.
export const FILTERS_STORAGE_KEY = 'sales_task_list_filters_v1';

// Current schema version. A blob stamped with an OLDER known version is migrated
// forward (MIGRATIONS below); anything else — a newer version written by a future
// deploy, or a missing/garbage `v` — is discarded wholesale.
export const FILTERS_SCHEMA_VERSION = 4;

// Task types renamed on 2026-07-31 (the persisted task_type values changed with
// them — see docs/migrations/2026-07-31-sales-task-list-due-datetime.sql). A blob
// written before the rename carries the old value, so migrate it forward instead
// of silently resetting the user's filter to "All". Values that are neither
// current nor in this map are dropped.
const TASK_TYPE_RENAMES = {
  NO_JOIN: 'BOOK_NO_JOIN',
  NO_MEMBERSHIP: 'JOIN_NO_MEMBERSHIP',
  TRIAL_RESET_NO_JOIN: 'TRIAL_RESET_BOOK_NO_JOIN',
  TRIAL_RESET_NO_MEMBERSHIP: 'TRIAL_RESET_JOIN_NO_MEMBERSHIP'
};

// The pre-multi-select sentinel for "no type filter". Still recognised on read so
// an old blob (or a hand-edited one) does not resolve to a literal task type.
const ALL_SENTINEL = 'All';

/*
 * v1 → v2: type_filter was a single string, either the 'All' sentinel or one
 * task_type. It becomes an array: 'All' (and anything falsy or non-string) maps
 * to [] — "no type filter" — and a real value maps to a one-element selection.
 * The rename map is applied by the normal read path afterwards, so it does not
 * need repeating here.
 */
const migrate_v1 = (blob) => {
  const single = blob.type_filter;
  const type_filter =
    typeof single === 'string' && single !== '' && single !== ALL_SENTINEL ? [single] : [];
  return { ...blob, v: 2, type_filter };
};

// v2 → v3: the assignee filter no longer exists; scope and owner start at their
// defaults.
const migrate_v2 = (blob) => {
  const { agent_filter, ...rest } = blob;
  return { ...rest, v: 3 };
};

// v3 → v4: the call-eligibility filter starts at "any".
const migrate_v3 = (blob) => ({ ...blob, v: 4, call_filter: '' });

// Keyed by the version being migrated FROM. Applied in ascending order until the
// blob reaches FILTERS_SCHEMA_VERSION.
const MIGRATIONS = { 1: migrate_v1, 2: migrate_v2, 3: migrate_v3 };

// Coerce a multi-select value to the array stored since v2. A bare string (a caller
// still on the v1 shape) becomes a one-element selection; anything else empties.
const normalise_selection = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value !== '' && value !== ALL_SENTINEL) return [value];
  return [];
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// A persisted date bound is either a YYYY-MM-DD day or '' (the cleared range).
const valid_date_value = (v) => typeof v === 'string' && (v === '' || DATE_RE.test(v));

// The date picker only ever emits both bounds together, so a half-set range is
// corrupt rather than meaningful.
const valid_date_range = (from, to) =>
  valid_date_value(from) && valid_date_value(to) && (from === '') === (to === '');

/*
 * Read and validate the stored filters.
 *
 * Returns null when there is nothing usable (caller falls back to its defaults),
 * otherwise an object containing only the keys that validated. A present-but-
 * invalid key is omitted so the caller's default applies to that filter alone,
 * leaving the user's other saved choices intact.
 *
 * status_values / type_values / scope_values / call_values are the offered option
 * values, passed in so this module stays independent of the page's UI lists.
 */
export function read_persisted_filters({
  status_values = [],
  type_values = [],
  scope_values = [],
  call_values = []
} = {}) {
  let parsed;
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    parsed = JSON.parse(raw);
  } catch {
    // Unparseable JSON, or localStorage unavailable (private mode).
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  // Upgrade an older blob step by step. A version with no migration (including a
  // missing or newer one) is unusable — fall back to the caller's defaults.
  let blob = parsed;
  while (blob.v !== FILTERS_SCHEMA_VERSION) {
    const migrate = MIGRATIONS[blob.v];
    if (!migrate) return null;
    blob = migrate(blob);
  }

  const out = {};

  if (status_values.includes(blob.status_filter)) {
    out.status_filter = blob.status_filter;
  }

  // type_filter is a set of task types; [] means "all", and an explicitly
  // emptied filter is a real choice that must survive a reload.
  // Renamed types are migrated forward; unknown ones are dropped individually so
  // one stale entry cannot discard the whole selection.
  if (Array.isArray(blob.type_filter)) {
    out.type_filter = blob.type_filter
      .filter((v) => typeof v === 'string' && v !== '' && v !== ALL_SENTINEL)
      .map((v) => TASK_TYPE_RENAMES[v] || v)
      .filter((v, i, arr) => type_values.includes(v) && arr.indexOf(v) === i);
  }

  if (scope_values.includes(blob.scope_filter)) {
    out.scope_filter = blob.scope_filter;
  }

  // Owner uuids are not checked against the current owner list: that list is
  // loaded later. [] is kept — it means "all owners", explicitly chosen.
  if (Array.isArray(blob.owner_filter)) {
    out.owner_filter = [
      ...new Set(blob.owner_filter.filter((v) => typeof v === 'string' && v !== ''))
    ];
  }

  // '' is "any", a real choice like an emptied selection.
  if (blob.call_filter === '' || call_values.includes(blob.call_filter)) {
    out.call_filter = blob.call_filter;
  }

  if (blob.date_range === null) {
    out.date_range = null; // untouched → caller keeps its rolling default
  } else if (blob.date_range && typeof blob.date_range === 'object') {
    const { from, to } = blob.date_range;
    if (valid_date_range(from, to)) out.date_range = { from, to };
  }

  return out;
}

/*
 * Persist the filter set. Best-effort: quota or serialisation failures are
 * swallowed (returns false) because persistence must never break filtering.
 *
 * Pass date_range as null when the range is still the untouched rolling default.
 */
export function write_persisted_filters({
  status_filter,
  type_filter,
  scope_filter,
  owner_filter,
  call_filter,
  date_range
} = {}) {
  try {
    localStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({
        v: FILTERS_SCHEMA_VERSION,
        status_filter,
        // Multi-selects normalise to an array so a caller passing undefined (or a
        // stale single value) cannot write a v1-shaped blob.
        type_filter: normalise_selection(type_filter),
        scope_filter: typeof scope_filter === 'string' ? scope_filter : 'all',
        owner_filter: normalise_selection(owner_filter),
        call_filter: typeof call_filter === 'string' ? call_filter : '',
        date_range: date_range || null
      })
    );
    return true;
  } catch {
    return false;
  }
}
